import React__default from "react";
import { useFloating, useListNavigation, useTypeahead, useInteractions, useListItem } from "@floating-ui/react";
import { useListboxContext } from "./listbox-context.mjs";
const compareString = (a, b) => a === b;
const useListbox = ({
  defaultValue,
  value: propValue,
  onChange,
  valueCompareFn = compareString,
  disabled = false
}) => {
  const [innerValue, setInnerValue] = React__default.useState(defaultValue);
  const value = onChange && typeof propValue !== "undefined" ? propValue : innerValue;
  const [activeIndex, setActiveIndex] = React__default.useState(null);
  const { context, refs } = useFloating({
    open: !disabled
    // Always open floating hook, so we could use interaction hooks.
  });
  const elementsRef = React__default.useRef([]);
  const labelsRef = React__default.useRef([]);
  const [values, setValues] = React__default.useState([]);
  const selectedIndex = value ? values.findIndex((v) => valueCompareFn(v, value)) : null;
  const listNav = useListNavigation(context, {
    enabled: !disabled,
    listRef: elementsRef,
    activeIndex,
    selectedIndex,
    onNavigate: setActiveIndex,
    focusItemOnHover: false,
    focusItemOnOpen: false
  });
  const typeahead = useTypeahead(context, {
    enabled: !disabled,
    listRef: labelsRef,
    activeIndex,
    selectedIndex,
    onMatch: setActiveIndex
  });
  const { getFloatingProps: getCoreFloatingProps, getItemProps } = useInteractions([listNav, typeahead]);
  const handleSelect = React__default.useCallback(
    (nextValue) => {
      if (typeof propValue === "undefined") {
        setInnerValue(nextValue);
      }
      if (onChange) {
        onChange(nextValue);
      }
    },
    [propValue, onChange]
  );
  const registerValue = React__default.useCallback((value2, index) => {
    setValues((prevValues) => {
      const newArray = [...prevValues];
      newArray[index] = value2;
      return newArray;
    });
  }, []);
  const unregisterValue = React__default.useCallback((value2) => {
    setValues((prevValues) => {
      return [...prevValues].filter((i) => i !== value2);
    });
  }, []);
  const getFloatingProps = React__default.useCallback(
    (userProps) => {
      return getCoreFloatingProps({
        ...userProps,
        role: "listbox"
      });
    },
    [getCoreFloatingProps]
  );
  return React__default.useMemo(
    () => ({
      activeIndex,
      selectedIndex,
      getFloatingProps,
      getItemProps,
      handleSelect,
      registerValue,
      unregisterValue,
      refs,
      elementsRef,
      labelsRef,
      disabled
    }),
    [activeIndex, selectedIndex, getFloatingProps, getItemProps, handleSelect, registerValue, unregisterValue, refs, elementsRef, labelsRef, disabled]
  );
};
const useListboxItem = ({ label, value }) => {
  const { activeIndex, selectedIndex, handleSelect, getItemProps: getCoreItemProps, registerValue, unregisterValue, disabled } = useListboxContext();
  const { ref, index } = useListItem({ label });
  const active = activeIndex === index;
  const selected = index !== -1 && selectedIndex === index;
  const focusable = React__default.useMemo(() => {
    if (disabled) {
      return false;
    }
    if (activeIndex !== null) {
      return active;
    }
    return selectedIndex !== null ? selected : index === 0;
  }, [active, activeIndex, selected, selectedIndex, index, disabled]);
  React__default.useLayoutEffect(() => {
    registerValue(value, index);
    return () => {
      unregisterValue(value);
    };
  }, [index, value, registerValue, unregisterValue]);
  const getItemProps = React__default.useCallback(
    (userProps) => {
      return getCoreItemProps({
        ...userProps,
        role: "option",
        "aria-selected": selected,
        tabIndex: focusable ? 0 : -1,
        onClick: () => handleSelect(value),
        onKeyDown: (event) => {
          switch (event.key) {
            case " ":
            case "Enter":
              event.preventDefault();
              handleSelect(value);
              return;
          }
        }
      });
    },
    [getCoreItemProps, handleSelect, value, selected, focusable]
  );
  return React__default.useMemo(
    () => ({
      getItemProps,
      selected,
      ref
    }),
    [getItemProps, selected, ref]
  );
};
export {
  useListbox,
  useListboxItem
};
